// Centralises the network primitives the dialog modules need:
//
//   • post(url, payload) — fire a CSRF-protected JSON POST, return parsed JSON
//   • del(url)           — fire a CSRF-protected DELETE, throw on failure
//   • get(url)           — JSON GET, returning null on any error
//   • save({ ... })      — POST or dummy-by-id fallback (used by address-form)
//   • load(id, template) — GET-by-template or dummy lookup     (used by address-form)
//
// `save` / `load` are the id-based high-level helpers — useful for resources
// the backend identifies by an id the JS doesn't know up-front (addresses).
// Resources without a meaningful list-level id (recipients) use the lower-
// level `post` / `get` directly and manage their own dummy keying.

import { fetchWithRedirectGuard } from './http.js';

const csrfToken  = document.querySelector('meta[name="_csrf"]')?.content;
const csrfHeader = document.querySelector('meta[name="_csrf_header"]')?.content || 'X-CSRF-TOKEN';

// ── Low-level primitives ────────────────────────────────────────────────────

export async function post(url, payload) {
  const headers = { 'Content-Type': 'application/json' };
  if (csrfToken) headers[csrfHeader] = csrfToken;
  const res = await fetchWithRedirectGuard(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Try to surface a Spring `{ errors: [{ field?, message }] }` body so
    // callers can decorate the offending field. Falls back to a plain Error
    // when the body isn't JSON or doesn't follow the shape.
    let body = null;
    try { body = await res.json(); } catch { /* not JSON */ }
    const err = new Error(`Save failed (${res.status})`);
    if (Array.isArray(body?.errors)) err.errors = body.errors;
    throw err;
  }
  return res.json();
}

export async function del(url) {
  const headers = {};
  if (csrfToken) headers[csrfHeader] = csrfToken;
  const res = await fetchWithRedirectGuard(url, { method: 'DELETE', headers });
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch { /* not JSON, or no body */ }
    const err = new Error(`Delete failed (${res.status})`);
    if (Array.isArray(body?.errors)) err.errors = body.errors;
    throw err;
  }
}

export async function get(url) {
  try {
    const res = await fetchWithRedirectGuard(url);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Load failed:', err);
    return null;
  }
}

// ── Id-based save / load (address-form) ─────────────────────────────────────

const dummyStore = new Map();
let dummyIdCounter = 0;

// Pre-integration seed for the dummy store — sample customer addresses so
// the address book has something realistic to render before the Spring
// backend exists. Once a real load URL is wired up, the address book
// goes through fetch and ignores this seed entirely.
const DUMMY_ADDRESSES = [
  { id: 'a1',  firstName: 'Ladoo',    lastName: 'McLadooson', addressLine1: '100 Ladoo Lane',          addressLine2: '',         addressLine3: '',          city: 'Boondishire', postcode: 'LA4 2OO',  country: 'GB' },
  { id: 'a2',  firstName: 'Aanya',    lastName: 'Sharma',     addressLine1: '42 High Street',          addressLine2: 'Flat 3',   addressLine3: '',          city: 'London',      postcode: 'SW1A 1AA', country: 'GB' },
  { id: 'a3',  firstName: 'Ravi',     lastName: 'Patel',      addressLine1: '7 Oakwood Avenue',        addressLine2: '',         addressLine3: '',          city: 'Manchester',  postcode: 'M1 2AB',   country: 'GB' },
  { id: 'a4',  firstName: 'Priya',    lastName: 'Singh',      addressLine1: '15 Cedar Close',          addressLine2: '',         addressLine3: '',          city: 'Birmingham',  postcode: 'B12 9XY',  country: 'GB' },
  { id: 'a5',  firstName: 'Arjun',    lastName: 'Khan',       addressLine1: '88 Elm Road',             addressLine2: '',         addressLine3: '',          city: 'Leeds',       postcode: 'LS2 7PQ',  country: 'GB' },
  { id: 'a6',  firstName: 'Meera',    lastName: 'Iyer',       addressLine1: '3 Birch Mews',            addressLine2: '',         addressLine3: '',          city: 'Bristol',     postcode: 'BS1 4DR',  country: 'GB' },
  { id: 'a7',  firstName: 'Vikram',   lastName: 'Desai',      addressLine1: '21 Willow Crescent',      addressLine2: '',         addressLine3: '',          city: 'Edinburgh',   postcode: 'EH3 6QQ',  country: 'GB' },
  { id: 'a8',  firstName: 'Anita',    lastName: 'Kapoor',     addressLine1: '56 Maple Court',          addressLine2: '',         addressLine3: '',          city: 'Cardiff',     postcode: 'CF10 3NB', country: 'GB' },
  { id: 'a9',  firstName: 'Eleanor',  lastName: 'Hawthorne',  addressLine1: '14 Pembroke Square',      addressLine2: 'Apt 5B',   addressLine3: '',          city: 'London',      postcode: 'W8 6PA',   country: 'GB' },
  { id: 'a10', firstName: 'Callum',   lastName: 'MacGregor',  addressLine1: '9 Princes Crescent',      addressLine2: '',         addressLine3: '',          city: 'Glasgow',     postcode: 'G12 9JN',  country: 'GB' },
  { id: 'a11', firstName: 'Bronwyn',  lastName: 'Llewellyn',  addressLine1: '27 Castle Lane',          addressLine2: '',         addressLine3: '',          city: 'Swansea',     postcode: 'SA1 5DQ',  country: 'GB' },
  { id: 'a12', firstName: 'Ishaan',   lastName: 'Reddy',      addressLine1: 'Flat 304, Lotus Heights', addressLine2: 'MG Road',  addressLine3: '',          city: 'Bengaluru',   postcode: '560001',   country: 'IN' },
  { id: 'a13', firstName: 'Saanvi',   lastName: 'Mehta',      addressLine1: 'B-12 Greenwood Apartments', addressLine2: 'Linking Road', addressLine3: 'Bandra West', city: 'Mumbai', postcode: '400050', country: 'IN' },
  { id: 'a14', firstName: 'Aditya',   lastName: 'Gupta',      addressLine1: 'House No. 21, Sector 14', addressLine2: '',         addressLine3: '',          city: 'Gurugram',    postcode: '122001',   country: 'IN' },
  { id: 'a15', firstName: 'Diya',     lastName: 'Chatterjee', addressLine1: '5 Park Street',           addressLine2: 'Flat 2B',  addressLine3: '',          city: 'Kolkata',     postcode: '700016',   country: 'IN' },
  { id: 'a16', firstName: 'Kabir',    lastName: 'Joshi',      addressLine1: '88 Anna Salai',           addressLine2: '',         addressLine3: '',          city: 'Chennai',     postcode: '600002',   country: 'IN' },
  { id: 'a17', firstName: 'Camille',  lastName: 'Dubois',     addressLine1: '12 rue de Rivoli',        addressLine2: '',         addressLine3: '',          city: 'Paris',       postcode: '75001',    country: 'FR' },
  { id: 'a18', firstName: 'Lucas',    lastName: 'Moreau',     addressLine1: '47 avenue Jean Jaurès',   addressLine2: 'Apt 3',    addressLine3: '',          city: 'Lyon',        postcode: '69007',    country: 'FR' },
  { id: 'a19', firstName: 'Élodie',   lastName: 'Laurent',    addressLine1: '5 place Bellecour',       addressLine2: '',         addressLine3: '',          city: 'Marseille',   postcode: '13001',    country: 'FR' },
  { id: 'a20', firstName: 'Mathieu',  lastName: 'Bernard',    addressLine1: '23 rue Sainte-Catherine', addressLine2: '',         addressLine3: '',          city: 'Bordeaux',    postcode: '33000',    country: 'FR' },
];
DUMMY_ADDRESSES.forEach(a => dummyStore.set(a.id, a));

// POST `payload` to `saveUrl` and return the parsed JSON response.
//
// When `savedId` is present we splice it into the POST body as `id` so the
// server can distinguish "update existing" from "create new" without
// relying on the save URL itself encoding the id. The Spring controller
// dispatches on the presence of `id` in the request body.
//
// If `saveUrl` is false and `dummyPrefix` is set, generate (or reuse
// `savedId`) an id, stash `{ id, ...payload }` in the dummy store, and
// return it — lets the page work end-to-end before the Spring backend is
// wired up.
//
// If both `saveUrl` and `dummyPrefix` are falsy, returns null.
export async function save({ saveUrl, payload, savedId = null, dummyPrefix = null }) {
  if (saveUrl) {
    const body = savedId ? { id: savedId, ...payload } : payload;
    return post(saveUrl, body);
  }
  if (!dummyPrefix) return null;
  const id = savedId || `${dummyPrefix}-${++dummyIdCounter}`;
  const dummy = { id, ...payload };
  dummyStore.set(id, dummy);
  return dummy;
}

// GET the record with `id` from `loadUrlTemplate` (`{id}` substituted in).
// Returns the dummy-stored value if present, the parsed JSON on success, or
// null when the template is missing or the request fails.
export async function load(id, loadUrlTemplate) {
  if (!id) return null;
  if (dummyStore.has(id)) return dummyStore.get(id);
  if (!loadUrlTemplate) return null;
  return get(loadUrlTemplate.replace('{id}', encodeURIComponent(id)));
}

// Seed the dummy store with a record that wasn't created via save() —
// available if a caller needs to inject data outside the form flow.
// Mostly redundant now that the address-book reads through dummyAll(),
// but kept as a public hook.
export function dummyStash(record) {
  if (record?.id) dummyStore.set(record.id, record);
}

// All records currently in the dummy store, in insertion order. The
// address book uses this in pre-integration mode so any address saved
// via the form during the session shows up in the picker on next open,
// mirroring how the Spring backend will own the list of addresses.
export function dummyAll() {
  return Array.from(dummyStore.values());
}
